/**
 * Sprint 23 — language theme read/write.
 *
 * The active theme lives in `workspace_context.language_theme` (workspace-wide).
 * This hook reads it via MCP (cached by TanStack, shared across every consumer),
 * mirrors it to localStorage for flicker-free first paint, and exposes a setter
 * that persists + optimistically updates the cache. Default: Workforce.
 */

import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { useAuth } from "../../lib/hooks/use-auth";
import {
  DEFAULT_LANGUAGE_THEME,
  isLanguageTheme,
  type LanguageTheme,
} from "../lib/themed-labels";

const LS_PREFIX = "kuvio_language_theme_";

function lsKey(workspaceId: string | null): string | null {
  return workspaceId ? `${LS_PREFIX}${workspaceId}` : null;
}

function readLs(workspaceId: string | null): LanguageTheme | null {
  const key = lsKey(workspaceId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return isLanguageTheme(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeLs(workspaceId: string | null, theme: LanguageTheme): void {
  const key = lsKey(workspaceId);
  if (!key) return;
  try {
    localStorage.setItem(key, theme);
  } catch {
    /* ignore */
  }
}

export function languageThemeQueryKey(workspaceId: string | null) {
  return ["language-theme", workspaceId] as const;
}

export interface UseLanguageThemeResult {
  theme: LanguageTheme;
  setTheme: (next: LanguageTheme) => void;
  isSaving: boolean;
}

export function useLanguageTheme(): UseLanguageThemeResult {
  const { currentWorkspaceId } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: languageThemeQueryKey(currentWorkspaceId),
    queryFn: async (): Promise<LanguageTheme> => {
      const stored = await mcpClient.getWorkspaceContext("language_theme");
      const theme = isLanguageTheme(stored) ? stored : DEFAULT_LANGUAGE_THEME;
      writeLs(currentWorkspaceId, theme);
      return theme;
    },
    enabled: !!currentWorkspaceId,
    staleTime: 60_000,
    // Seed from localStorage so the first paint already uses the right vocabulary.
    initialData: () => readLs(currentWorkspaceId) ?? undefined,
  });

  const mutation = useMutation({
    mutationFn: (next: LanguageTheme) => mcpClient.setWorkspaceContext("language_theme", next),
    onSuccess: (_data, next) => {
      writeLs(currentWorkspaceId, next);
      qc.setQueryData(languageThemeQueryKey(currentWorkspaceId), next);
    },
  });

  const setTheme = useCallback((next: LanguageTheme) => mutation.mutate(next), [mutation]);

  const theme: LanguageTheme =
    query.data ?? readLs(currentWorkspaceId) ?? DEFAULT_LANGUAGE_THEME;

  return { theme, setTheme, isSaving: mutation.isPending };
}
